import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useHomeworks, useDeleteHomework } from '@/services/homeworks';
import { BottomTabBar } from '@/components/ui/BottomTabBar';
import { GlobalHeader } from '@/components/GlobalHeader';
import { Spinner } from '@/components/ui/Spinner';

export function HomeworkPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { childId } = useAuthStore();
  const { data, isLoading } = useHomeworks(childId ?? '');
  const { mutate: deleteHomework, isPending: isDeleting, variables: deletingId } = useDeleteHomework(childId ?? '');

  const carouselRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const homeworks = data?.homeworks ?? [];
  const open = homeworks.filter((h) => h.status === 'open');
  const done = homeworks.filter((h) => h.status === 'complete');

  const handleCarouselScroll = () => {
    if (!carouselRef.current) return;
    setActiveSlide(Math.round(carouselRef.current.scrollLeft / 292)); // 280px card + 12px gap
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader />
        <div className="app-content" style={{ padding: '16px', paddingBottom: '24px' }}>

          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 16px' }}>
            {t('homework.title')}
          </h1>

          {/* Add button */}
          <Link
            to="/student/homework/add"
            className="btn btn-primary"
            style={{ display: 'block', width: '100%', boxSizing: 'border-box', textAlign: 'center', textDecoration: 'none', marginBottom: '20px' }}
          >
            {t('homework.add_btn')}
          </Link>

          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '48px' }}>
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              {open.length === 0 && done.length === 0 && (
                <p style={{ textAlign: 'center', color: 'var(--text-muted)', paddingTop: '32px', fontSize: '0.9rem' }}>
                  {t('homework.empty')}
                </p>
              )}

              {/* ── Open homework carousel ── */}
              {open.length > 0 && (
                <>
                  <h2 style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                    {t('homework.open_section')}
                  </h2>

                  {/* Edge-to-edge wrapper escapes the 16px parent padding */}
                  <div className="-mx-4">
                    <div
                      ref={carouselRef}
                      className="no-scrollbar"
                      onScroll={handleCarouselScroll}
                      style={{
                        display: 'flex',
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        scrollBehavior: 'smooth',
                        WebkitOverflowScrolling: 'touch',
                        gap: '12px',
                        padding: '0 16px 12px 16px',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                      } as React.CSSProperties}
                    >
                      {open.map((hw) => {
                        const doneCount = hw.exercises.filter((e) => e.status === 'done').length;
                        const total = hw.exercises.length;
                        const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
                        const thisIsDeleting = isDeleting && deletingId === hw.homework_id;
                        return (
                          <div
                            key={hw.homework_id}
                            style={{
                              width: '280px',
                              maxWidth: '85vw',
                              flexShrink: 0,
                              scrollSnapAlign: 'center',
                              position: 'relative',
                              opacity: thisIsDeleting ? 0.4 : 1,
                              transition: 'opacity 0.2s',
                            }}
                          >
                            <Link
                              to={`/student/homework/${hw.homework_id}`}
                              className="card h-full"
                              style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column' }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', paddingRight: '24px' }}>
                                <span style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: '0.95rem' }}>{hw.subject}</span>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                  {doneCount}/{total} {t('homework.done')}
                                </span>
                              </div>
                              {hw.ocr_status === 'pending' && (
                                <p style={{ margin: '0 0 8px', fontSize: '0.72rem', color: 'var(--warning)' }}>
                                  {t('homework.ocr_pending')}
                                </p>
                              )}
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '12px', flex: 1 }}>
                                {hw.themes.map((th) => (
                                  <span
                                    key={th}
                                    style={{
                                      fontSize: '0.72rem', padding: '2px 8px', borderRadius: '999px',
                                      background: 'var(--primary-light)', color: 'var(--primary)',
                                    }}
                                  >
                                    {th}
                                  </span>
                                ))}
                              </div>
                              <div style={{ width: '100%', background: '#E5E7EB', borderRadius: '999px', height: '6px' }}>
                                <div
                                  style={{ width: `${pct}%`, background: 'var(--primary)', height: '6px', borderRadius: '999px', transition: 'width 0.3s' }}
                                />
                              </div>
                            </Link>

                            {/* Delete button — outside the Link, positioned in card corner */}
                            <button
                              type="button"
                              aria-label={t('homework.delete_aria', { subject: hw.subject })}
                              disabled={thisIsDeleting}
                              onClick={() => deleteHomework(hw.homework_id)}
                              style={{
                                position: 'absolute', top: '10px', right: '10px',
                                width: '22px', height: '22px',
                                background: 'rgba(255,255,255,0.95)',
                                border: '1px solid var(--danger)',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: 'var(--danger)',
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                lineHeight: 1,
                                zIndex: 1,
                                padding: 0,
                              }}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Dot indicators */}
                  {open.length > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '4px', marginBottom: '8px' }}>
                      {open.map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          aria-label={t('homework.item_aria', { index: idx + 1 })}
                          onClick={() => {
                            carouselRef.current?.scrollTo({ left: idx * 292, behavior: 'smooth' });
                          }}
                          style={{
                            width: activeSlide === idx ? '20px' : '8px',
                            height: '8px',
                            borderRadius: '4px',
                            border: 'none',
                            background: activeSlide === idx ? 'var(--primary)' : '#D1D5DB',
                            transition: 'all 0.2s',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ── Completed homework list ── */}
              {done.length > 0 && (
                <div style={{ marginTop: open.length > 0 ? '20px' : '0' }}>
                  <h2 style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                    {t('homework.completed_section')}
                  </h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {done.map((hw) => {
                      const thisIsDeleting = isDeleting && deletingId === hw.homework_id;
                      return (
                        <div
                          key={hw.homework_id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            opacity: thisIsDeleting ? 0.4 : 1, transition: 'opacity 0.2s',
                          }}
                        >
                          <div
                            className="card"
                            onClick={() => navigate(`/student/homework/${hw.homework_id}`)}
                            style={{ flex: 1, opacity: 0.6, cursor: 'pointer' }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: 600, color: 'var(--text-muted)', textDecoration: 'line-through' }}>{hw.subject}</span>
                              <span style={{ fontSize: '0.75rem', color: 'var(--success)', fontWeight: 600 }}>{t('homework.done_badge')}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                              {hw.themes.map((th) => (
                                <span key={th} style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '999px', background: '#F3F4F6', color: 'var(--text-muted)' }}>
                                  {th}
                                </span>
                              ))}
                            </div>
                          </div>
                          <button
                            type="button"
                            aria-label={t('homework.delete_aria', { subject: hw.subject })}
                            disabled={thisIsDeleting}
                            onClick={() => deleteHomework(hw.homework_id)}
                            style={{
                              flexShrink: 0,
                              background: 'none',
                              border: '1px solid var(--danger)',
                              borderRadius: '8px',
                              padding: '6px 10px',
                              cursor: 'pointer',
                              color: 'var(--danger)',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                            }}
                          >
                            {thisIsDeleting ? '…' : t('actions.delete')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

        </div>
        <BottomTabBar activeTab="Home" />
      </div>
    </div>
  );
}
